'use client';

import React from 'react';
import { CornerDownRight } from 'lucide-react';
import type { DocumentSearchContext } from '@paeffgen/react/crm-documents/data-access-graphql';
import { HighlightText } from '@paeffgen/react/crm-documents/ui';
import { formatQuantity } from '@paeffgen/react/crm-documents/util';

/**
 * NAV nummeriert Positionen in Zehntausenderschritten (10000, 20000, …), was
 * als „Pos 1", „Pos 2" lesbar ist. Zwischennummern (10500) kommen vor und
 * dürfen nicht als „Pos 1.05" erscheinen — dann bleibt der Rohwert stehen.
 * Die Beleg-Detailsicht zeigt die Positionsnummer nirgends an, es gibt also
 * keine bestehende Konvention, an die man sich sonst halten könnte.
 */
function positionLabel(lineNumber: string): string {
  const raw = Number(lineNumber);
  if (!Number.isFinite(raw) || raw === 0) return lineNumber;
  const short = raw / 10000;
  return Number.isInteger(short) ? String(short) : lineNumber;
}

interface DocumentSearchContextLinesProps {
  /**
   * null = Kopf-Treffer (Suchbegriff steckt in Belegnummer/Kommission).
   * undefined = Feld wurde nicht abgefragt (Umschalter aus).
   * Beide Fälle rendern nichts.
   */
  context: DocumentSearchContext | null | undefined;
}

/**
 * Treffer-Positionen unterhalb einer Belegzeile — die Begründung, warum der
 * Beleg in der Trefferliste steht. Nur sichtbar, wenn der Nutzer die Ansicht
 * zugeschaltet hat.
 *
 * Ohne eigene Event-Handler: der Klick wird von der Belegzeilen-Wurzel behandelt
 * (Bubbling), sodass auch ein Klick auf die Positionen den Beleg öffnet. Der
 * Long-Press für den Auswahlmodus sitzt weiterhin nur am oberen Kartenteil.
 */
export function DocumentSearchContextLines({
  context,
}: DocumentSearchContextLinesProps) {
  if (!context || context.lines.length === 0) return null;

  const hidden = context.totalCount - context.lines.length;

  return (
    <div className="px-3 pb-2.5 md:px-4 md:pb-3 space-y-1">
      {context.lines.map((line) => (
        <div
          key={line.lineNumber}
          // Ab md dasselbe 12-Spalten-Raster wie die Belegzeile
          // (document-row.tsx: col-span 3+2+4+3, Betrag in 10–12) — nur so
          // fluchtet die Menge unter dem Betrag bei jeder Fensterbreite.
          // Ein festes Padding würde das nicht treffen, weil eine
          // Rasterspalte eine Bruchteilsbreite ist. Unter md bleibt das
          // ursprüngliche Flex-Layout unverändert.
          className="flex items-baseline gap-2 text-xs text-zinc-600 md:grid md:grid-cols-12 md:gap-4"
        >
          {/* flex-wrap unter md: Pos-Label, Artikelnummer und Hersteller sind
              shrink-0 und reservieren ihre Breite — auf dem Telefon bliebe für
              die Beschreibung sonst nichts übrig. Mit der Mindestbreite unten
              übersteigt die Summe der Elemente dort die Zeile, und der
              Hersteller (das letzte Element) rutscht auf eine zweite Zeile.
              Ab md passt alles nebeneinander, deshalb nowrap. */}
          <div className="flex flex-wrap items-baseline gap-2 min-w-0 flex-1 md:flex-nowrap md:col-span-9">
            <CornerDownRight
              size={12}
              className="shrink-0 mt-0.5 text-zinc-400"
              aria-hidden="true"
            />
            <span className="text-zinc-400 tabular-nums shrink-0">
              Pos {positionLabel(line.lineNumber)}
            </span>
            {/* Artikelnummer vor der Beschreibung — Hauptfall, wenn der
                Suchtreffer über article_no zustande kam (Typesense
                article_nos, Gewicht 10) und die Beschreibung selbst keinen
                sichtbaren Grund für den Treffer zeigt. Nullable: entfällt
                bei null ersatzlos (HighlightText rendert dann nichts). */}
            <HighlightText
              text={line.articleNumber}
              className="font-mono text-zinc-500 shrink-0"
            />
            <span className="min-w-[12rem] max-w-full flex-1 truncate">
              <HighlightText text={line.description} />
              {/* Zweite Beschreibungszeile inline: Positionen unterscheiden
                  sich oft ausschließlich darin (N08V1060 "links" vs. N08V1061
                  "rechts" bei identischer Bezeichnung). Sie bleibt in der
                  gemeinsamen truncate-Spanne, damit die Zeilenhöhe stimmt. */}
              {line.description2 && (
                <span className="text-zinc-400">
                  {' · '}
                  <HighlightText text={line.description2} />
                </span>
              )}
            </span>
            {/* Hersteller am Ende der Zeile. Er erklärt den Markentreffer —
                die Marke kommt im angezeigten Text nicht vor, weil Navision
                sie abkürzt ("RHZ Dachrinne"). Die Bezeichnung hat Vorrang:
                sie identifiziert die Position, der Hersteller ist in allen
                Trefferzeilen derselbe. */}
            {line.manufacturer && (
              <span className="flex items-baseline gap-2 shrink-0">
                <span className="text-zinc-300" aria-hidden="true">
                  |
                </span>
                <HighlightText
                  text={line.manufacturer}
                  className="text-zinc-500"
                />
              </span>
            )}
          </div>
          {line.quantity != null && (
            // whitespace-nowrap: gemessene Extremwerte im Bestand ("2.250
            // Blister"/"2.250 Flasche", ~13 Zeichen) brechen bei knapper Spalte
            // auf schmalem Desktop sonst um zweizeilig — Überlaufen ist hier
            // das kleinere Problem, weil ein Zeilenumbruch die Zeilenhöhe der
            // ganzen Positionszeile sprengt.
            <span className="shrink-0 tabular-nums text-zinc-500 md:col-span-3 md:text-right whitespace-nowrap">
              {formatQuantity(line.quantity)}
              {line.unit ? ` ${line.unit}` : ''}
            </span>
          )}
        </div>
      ))}

      {hidden > 0 && (
        <div className="pl-5 text-xs text-zinc-400">
          +{hidden} weitere {hidden === 1 ? 'Position' : 'Positionen'}
        </div>
      )}
    </div>
  );
}
